import React, { useState, memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import {
  AccessTimeRounded as ClockIcon,
  DoneRounded as CheckIcon,
  DoneAllRounded as CheckAllIcon,
  ErrorRounded as ErrorIcon,
  StarBorderRounded as StarIcon,
  StarRounded as BookmarkedIcon,
  FileCopyRounded as FileIcon,
  GetApp as SaveIcon,
} from '@material-ui/icons'
import { Divider, Badge } from '@material-ui/core'

import { useTheme } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'

import { getSender } from '../../../utils/messages'

import { setConversations } from '../../../redux/conversations/actions'
import { setGalleryOpen } from '../../../redux/gallery/actions'

import messages from '../../../services/messages'
import AudioPlayer from '../feedback/audioPlayer'
import Typography from '../display/typography'
import Highlight from '../display/highlight'
import HighlightSearch from '../display/highlightSearch'
import IconButton from '../inputs/iconButton'
import CircularProgress from '../feedback/circularProgress'

import useStyles from './balloonAgentStyle'
import handleDownload from '../../../utils/downloadFile'

const BalloonAgent = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const size = useSelector((state) => state.preferences)
  const theme = useTheme()
  let useStylesParams = {
    size: size,
    resize: props?.resize,
  }
  const classes = useStyles(useStylesParams)
  const user = useSelector((state) => state.user)
  const activeConversation = useSelector((state) => state.activeConversation)
  const conversation = useSelector(
    (state) => state.conversations.conversations[activeConversation]
  )
  const messageStore = useSelector(
    (state) => state.conversations.messages[activeConversation]
  )
  const chatKeyword = useSelector((state) => state.keyword)
  const [showActions, setShowActions] = useState(false)
  const [bookmarked, setBookmarked] = useState(props.data.favorite)
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const copyToClippboard = (string) => {
    navigator.clipboard.writeText(string).then(
      () => {
        enqueueSnackbar(`${t('commons.copied')}`, {
          variant: 'success',
        })
      },
      (err) => {
        console.error(t('commons.copiedError'), err)
      }
    )
  }

  const handleFavorite = () => {
    setLoading(true)
    messages({
      method: 'patch',
      url: `/tech4h-messages/message/favorite/${props.data.currentId}`,
      data: {
        favorite: bookmarked ? false : true,
      },
    })
      .then((response) => {
        setLoading(false)
        setBookmarked(bookmarked ? false : true)
        let msgs = messageStore
        for (let i = 0; i < msgs.length; i++) {
          if (msgs[i].currentId === props?.data?.currentId) {
            msgs[i].favorite = !bookmarked
            break
          }
        }
        conversation.message = msgs
        dispatch(setConversations(conversation))
      })
      .catch((error) => {})
  }

  const renderActions = () => {
    if (props.showBookmark && showActions && props.data.currentId) {
      return (
        <div className={classes.actions}>
          <IconButton
            size="small"
            color="primary"
            component="span"
            disabled={loading}
            style={{ width: 32, height: 32 }}
            onClick={() => handleFavorite()}
          >
            {bookmarked ? (
              loading ? (
                <CircularProgress size={16} />
              ) : (
                <BookmarkedIcon fontSize="inherit" />
              )
            ) : loading ? (
              <CircularProgress size={16} />
            ) : (
              <StarIcon fontSize="inherit" />
            )}
          </IconButton>
          {props?.data?.files ? (
            <IconButton
              size="small"
              color="primary"
              component="span"
              style={{ width: 32, height: 32, marginTop: 5 }}
              onClick={() =>
                handleDownload(
                  props.data.files.file_url,
                  props.data.files.file_name
                )
              }
            >
              <SaveIcon
                fontSize="inherit"
                style={{ transform: 'scaleX(-1)' }}
              />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              color="primary"
              component="span"
              style={{ width: 32, height: 32, marginTop: 5 }}
              onClick={(e) => {
                e.stopPropagation()
                copyToClippboard(props.data.text)
              }}
            >
              <FileIcon
                fontSize="inherit"
                style={{ transform: 'scaleX(-1)' }}
              />
            </IconButton>
          )}
          {/* <IconButton
            size="small"
            color="primary"
            component="span"
            style={{ width: 32, height: 32, marginTop: 5 }}
          >
            <RepplyIcon
              fontSize="inherit"
              style={{ transform: 'scaleX(-1)' }}
            />
          </IconButton> */}
        </div>
      )
    } else {
      return <></>
    }
  }

  const renderBookmarked = () => {
    if (bookmarked && !showActions) {
      return (
        <div className={classes.bookmarked} id="bookmarked">
          <BookmarkedIcon
            fontSize="inherit"
            style={{
              fontSize: 18,
              color: theme.palette.primary.main,
            }}
          />
        </div>
      )
    } else {
      return <></>
    }
  }

  const renderPrivateIcon = () => {
    if (props?.data?.private)
      return (
        <div className={classes.private}>
          <svg viewBox="0 0 10 9">
            <path d="M7.45 7.54A4.585 4.585 0 01.492 4.5c.18-.97.668-1.857 1.393-2.527L.58.67l.59-.59 8.25 8.25-.59.59-1.38-1.38zM2.474 2.564A3.735 3.735 0 001.343 4.5a3.752 3.752 0 005.5 2.433l-.845-.845a1.875 1.875 0 01-2.586-2.586l-.94-.94zM5.38 5.47L4.03 4.12a1.042 1.042 0 001.35 1.35zm3.289.943l-.597-.595c.279-.397.477-.845.584-1.318A3.752 3.752 0 003.98 1.724l-.658-.657A4.585 4.585 0 019.507 4.5a4.561 4.561 0 01-.837 1.913zM4.885 2.628a1.875 1.875 0 011.987 1.988L4.884 2.628z" />
          </svg>
          <Typography variant="body2">{t('commons.private')}</Typography>
        </div>
      )
  }

  const renderIcon = () => {
    const status = props?.data?.status // STATUS: sending, sent, delivered, read, error

    return (
      <>
        {status?.sending ? (
          <ClockIcon
            style={{
              fontSize: 14,
              marginRight: 5,
              marginBottom: -2,
            }}
          />
        ) : status?.sent ? (
          <CheckIcon
            style={{
              fontSize: 14,
              marginRight: 5,
              marginBottom: -2,
              color: theme.palette.primary.main,
            }}
          />
        ) : status?.read ? (
          // TODO: update this icon when it's implemented
          <CheckAllIcon
            style={{
              fontSize: 14,
              marginRight: 5,
              marginBottom: -2,
              color: theme.palette.primary.main,
            }}
          />
        ) : status?.error ? (
          <ErrorIcon
            style={{
              fontSize: 14,
              marginRight: 5,
              marginBottom: -2,
              color: theme.palette.error.main,
            }}
          />
        ) : (
          <CheckAllIcon
            style={{
              fontSize: 14,
              marginRight: 5,
              marginBottom: -2,
              color: theme.palette.primary.main,
            }}
          />
        )}
      </>
    )
  }

  const renderHeader = () => {
    return (
      <>
        <div className={classes.header}>
          {renderPrivateIcon()}
          <Typography variant="body1" component="h4">
            <span
              style={{
                marginRight: 6,
                color:
                  theme.palette.type === 'dark'
                    ? '#fff'
                    : theme.palette.primary.secondary,
              }}
            >
              {renderIcon()}
              {moment(props?.data?.sentTime).format('HH:mm')}
            </span>
            {getSender(props?.data, props?.userList ?? conversation?.users) ?? user?.name}
          </Typography>
        </div>
      </>
    )
  }

  const renderPicture = () => {
    if (props?.data?.files) {
      if (
        props.data.files.mime_type?.split('/')[0] !== 'audio' &&
        props.data.files.file_url
      ) {
        return (
          <>
            <div className={'anchor-midia'}>
              {props.data.files.mime_type?.split('/')[0] === 'image' ? (
                <img
                  className={classes.picture}
                  src={props.data.files.thumb_url}
                  alt=""
                  onClick={() => dispatch(setGalleryOpen(true))}
                />
              ) : (
                <a href={props.data.files.file_url} target="_blank">
                  <img
                    className={classes.picture}
                    src={props.data.files.thumb_url}
                    alt=""
                  />
                </a>
              )}
            </div>
          </>
        )
      }
    }
  }

  const renderAudio = () => {
    if (props?.data?.files) {
      if (
        props.data.files.mime_type?.split('/')[0] === 'audio' &&
        props.data.files.file_url
      ) {
        return (
          <>
            <div className={'anchor-audio'}>
              <AudioPlayer
                activeConversation={activeConversation}
                src={props.data.files.file_url}
                type={props.data.files.mime_type?.split(';')[0]}
              />
            </div>
          </>
        )
      }
    }
  }

  const renderPrivateText = () => {
    if (props.data?.private_note_operator != null) {
      return (
        <>
          <Divider style={{ width: '80%', marginTop: 5, marginBottom: 5 }} />
          <Badge
            badgeContent={t('commons.private')}
            color="primary"
            style={{ width: '100%', paddingRight: '1.5em' }}
          >
            <span className={'escapeDragScroll'} style={{ cursor: 'text' }}>
              <Highlight text={props.data.private_note_operator || ""} />
            </span>
          </Badge>
        </>
      )
    }
    return <></>
  }

  const renderText = () => {
    if (props.data.text) {
      if (chatKeyword) {
        return (
          <>
            <HighlightSearch
              text={props.data.text}
              keyword={chatKeyword}
              anchor={props.anchor ? props.anchor : 'chat'}
              copy={false}
            />
          </>
        )
      } else {
        return <Highlight text={props.data.text} />
      }
    }
  }

  return (
    <>
      <div
        id={props.data.currentId}
        className={classes.root}
        onClick={props.onClick}
        style={props.style}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        {renderActions()}
        {props.showBookmark && renderBookmarked()}
        <div className={classes.child}>
          {renderHeader()}
          <Typography
            variant="body1"
            component="p"
            style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
          >
            <span className={'escapeDragScroll'} style={{ cursor: 'text' }}>
              {renderText()}
            </span>
            {renderPrivateText()}
          </Typography>
          {renderPicture()}
          {renderAudio()}
        </div>
      </div>
    </>
  )
}

export default memo(BalloonAgent)
